/**
 * Modding (`08-modding.md`).
 *
 * The screen a mod is made on. It opens on a question — which game? — because every other
 * question it can ask has a different answer per game: what a quest is, which crops there are,
 * what a mod may patch, which API versions it may call. An editor opened before the game was
 * chosen would be an editor that knows none of that.
 *
 * Once a game is chosen there are four tabs, which are the four things modding a game is:
 *
 * | Tab | Is |
 * | --- | --- |
 * | **Content** | The game's data as a spreadsheet: a sheet per object type, a row per object |
 * | **Code** | The script editor, on the game's folder — hooks, scripts, the whole project |
 * | **Mods** | The mods already in the folder, and how a new one starts |
 * | **Reference** | The language, written down, with a Run button on it |
 *
 * **Free for anyone signed in.** A membership buys the Play button and the Discord role. It does
 * not buy the right to mod a game you already own, and charging for that would be charging people
 * to make our games better.
 *
 * Content and Code need a real filesystem, so they are desktop only and the web build says so.
 * The Reference needs nothing, so it is everywhere: it is the half somebody reads before they
 * have decided to install anything.
 */

import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { NEW_SCHEMA_TEMPLATE, editCount } from '@goodbuild/gbs-lang';

import { Badge, Button, Card, Eyebrow, Heading, Icon, PageHead, Text } from '../ui/index.js';
import { useAccess } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { TopBarSlot, useFullBleed } from '../components/layout/ScreenChrome.js';
import { ContentTab } from '../components/modding/Content.js';
import { GamePicker } from '../components/modding/GamePicker.js';
import { Mods } from '../components/modding/Mods.js';
import { Reference } from '../components/scripting/Reference.js';
import { Workbench } from '../components/scripting/Workbench.js';
import { useContent } from '../modding/content.js';
import { useWorkspace } from '../scripting/workspace.js';
import { capabilities, isNative } from '../platform/index.js';

const TABS = ['content', 'code', 'mods', 'reference'] as const;
type Tab = (typeof TABS)[number];

function isTab(value: unknown): value is Tab {
  return TABS.includes(value as Tab);
}

const TAB_LABELS: Readonly<Record<Tab, string>> = {
  content: 'Content',
  code: 'Code',
  mods: 'Mods',
  reference: 'Reference',
};

const TAB_ICONS = {
  content: 'library',
  code: 'code',
  mods: 'games',
  reference: 'book',
} as const;

export const Route = createFileRoute('/modding')({
  component: ModdingPage,
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } =>
    isTab(search.tab) ? { tab: search.tab } : {},
});

function ModdingPage() {
  const navigate = useNavigate();
  const { signedIn } = useAccess();
  const search = Route.useSearch();

  // One workspace for the whole screen. The grid and the editor are two views of one folder, and
  // two hooks would read the disk twice and disagree about it in between.
  const workspace = useWorkspace();
  const content = useContent(workspace);

  const tab: Tab = search.tab ?? (capabilities.editScripts ? 'content' : 'reference');
  const goTo = React.useCallback(
    (next: Tab) => void navigate({ to: '/modding', search: { tab: next } }),
    [navigate],
  );

  const chosen = workspace.open;
  const canEdit = capabilities.editScripts;
  // Content, Code and the Reference take the window: all three are tools with their own
  // navigation down one side, and a tool inside a reading column is a tool with less room than
  // the thing it is about. Mods is a page, so it keeps the padding every other screen has.
  const full =
    tab === 'reference' ||
    (chosen !== null && canEdit && tab === 'content') ||
    (chosen !== null && canEdit && tab === 'code');
  useFullBleed(full);

  const createMod = React.useCallback(
    async (folder: string, files: ReadonlyMap<string, string>) => {
      for (const [name, text] of files) await workspace.create(`${folder}/${name}`, text);
    },
    [workspace],
  );

  const createSchema = React.useCallback(() => {
    void workspace.create('content/game.gboschema', NEW_SCHEMA_TEMPLATE);
  }, [workspace]);

  const openFile = React.useCallback(
    (path: string) => {
      void workspace.openFile(path);
      goTo('code');
    },
    [workspace, goTo],
  );

  if (!signedIn) {
    return (
      <div className="gb-center">
        <Eyebrow tone="quiet">{'Modding'}</Eyebrow>
        <Heading size="5">{'Sign in to make a mod.'}</Heading>
        <Text color="gray" style={{ maxWidth: '40ch' }}>
          {
            'The editor, the data grid and the language reference are free for anyone with an account. There is no membership behind this.'
          }
        </Text>
        <Focusable
          autoFocus
          onSelect={() => void navigate({ to: '/signin' })}
          selectLabel="Sign in"
        >
          <Button size="3">{'Sign in'}</Button>
        </Focusable>
      </div>
    );
  }

  // The Reference reads on any build, so a machine with no filesystem opens on it rather than on
  // a tab that immediately says no.
  if (!canEdit && tab !== 'reference') return <GetTheApp onReference={() => goTo('reference')} />;

  const picking = chosen === null && tab !== 'reference';

  return (
    <FocusZone
      focusKey="modding-page"
      direction="column"
      className={full ? 'gb-scripting gb-scripting--full' : 'gb-stack gb-scripting'}
    >
      {/* The switch lives in the top bar, which is on every screen anyway, so the grid and the
          editor below it start at the top of the window. */}
      <TopBarSlot>
        <nav className="gb-scripting__nav" aria-label="Modding">
          {chosen === null ? null : (
            <Focusable
              onSelect={workspace.close}
              selectLabel="Choose another game"
              className="gb-focusable--flat gb-topbar__slot"
            >
              <button type="button" className="gb-modding__game" title="Choose another game">
                <Icon name="games" size={14} />
                <span>{chosen.label}</span>
                <Icon name="chevron-down" size={12} />
              </button>
            </Focusable>
          )}

          <FocusZone focusKey="modding-switch" direction="row" className="gb-scripting__tabs">
            {TABS.map((name) => (
              <Focusable
                key={name}
                onSelect={() => goTo(name)}
                selectLabel={TAB_LABELS[name]}
                className="gb-focusable--flat gb-topbar__slot"
              >
                <button
                  type="button"
                  className="gb-scripting__tab"
                  data-active={tab === name}
                  data-waiting={picking && name !== 'reference'}
                >
                  <Icon name={TAB_ICONS[name]} size={15} />
                  {TAB_LABELS[name]}
                  {name === 'content' && content.changes > 0 ? (
                    <span className="gb-scripting__tab-dot" aria-label="unsaved changes" />
                  ) : null}
                </button>
              </Focusable>
            ))}
          </FocusZone>
        </nav>
      </TopBarSlot>

      {tab === 'reference' ? (
        <Reference />
      ) : picking ? (
        <GamePicker
          onChoose={(game) => void workspace.openFolder(game.root)}
          onPickFolder={() => void workspace.pick()}
          onForget={(root) => void workspace.forget(root)}
        />
      ) : tab === 'content' ? (
        <ContentTab
          content={content}
          tickMilliseconds={content.set.shape.manifest.tickMilliseconds}
          onOpenFile={openFile}
          onCreateSchema={createSchema}
        />
      ) : tab === 'code' ? (
        <Workbench workspace={workspace} />
      ) : (
        <ModsTab
          workspace={workspace}
          content={content}
          onCreate={createMod}
          onOpenFile={openFile}
        />
      )}
    </FocusZone>
  );
}

/**
 * The Mods tab, with the heading that says what folder this is about.
 *
 * Split out so the tab has a head of its own: it is the one tab that is a page rather than a
 * tool, and a page with no title reads as a fragment of the one before it.
 */
function ModsTab({
  workspace,
  content,
  onCreate,
  onOpenFile,
}: {
  workspace: ReturnType<typeof useWorkspace>;
  content: ReturnType<typeof useContent>;
  onCreate: (folder: string, files: ReadonlyMap<string, string>) => Promise<void>;
  onOpenFile: (path: string) => void;
}) {
  const { shape } = content.set;
  const gameName = shape.manifest.name;
  const declared = content.set.types.file?.schemas.length ?? 0;

  return (
    <div className="gb-stack">
      <PageHead
        eyebrow="Mods"
        title={gameName === '' ? 'The mods in this folder' : `Mods for ${gameName}`}
        description="A mod changes a game two ways: hooks, which wrap a function the game already has, and patches, which change its data. Both live in one folder with a manifest, and both are compiled against the API versions that manifest declares."
      />

      <div className="gb-row">
        <Badge color={declared > 0 ? 'jade' : 'gray'}>
          {declared > 0
            ? `${String(declared)} object types a mod may hold`
            : 'this game declares no content types'}
        </Badge>
        {shape.assumedManifest ? (
          <Badge color="amber">
            {'no .gbsgame here, so the whole standard library is in scope'}
          </Badge>
        ) : null}
        {content.changes > 0 ? (
          <Badge color="amber">{`${String(editCount(content.edits))} unsaved data changes`}</Badge>
        ) : null}
      </div>

      <Mods
        set={content.set}
        content={content}
        gameName={gameName}
        modApis={shape.manifest.modApiSurface}
        onCreate={onCreate}
        onOpenFile={onOpenFile}
        onReveal={(path) => void workspace.showInFolder(path)}
      />
    </div>
  );
}

/**
 * What the web and the phone get instead of the editor.
 *
 * It says why rather than only saying no: everything here is saved to a folder on the machine it
 * is running on, and a browser tab has no folder to save to.
 */
function GetTheApp({ onReference }: { onReference: () => void }) {
  const navigate = useNavigate();

  return (
    <FocusZone focusKey="modding-cta" direction="column" className="gb-stack">
      <PageHead
        eyebrow="Modding"
        title="Modding runs in the desktop app."
        description="A mod is a folder on your machine: its manifest, its scripts and its content bundle. Everything written here is saved straight there and none of it goes to us, and a browser tab has no folder to save to."
      />

      <Card size="3">
        <Heading size="4">{'What you get'}</Heading>
        <ul className="gb-ref__list">
          <li>{'Pick a game you have installed and mod it — no setup, no SDK to download.'}</li>
          <li>
            {'Its data as a spreadsheet: every crop, quest and loot table the game declares.'}
          </li>
          <li>
            {
              'Edits become patches, so your mod sits beside somebody else’s rather than replacing it.'
            }
          </li>
          <li>
            {
              'The real compiler, checking your scripts as you type, with the same diagnostics CI gives.'
            }
          </li>
          <li>{'Nothing leaves the machine. No account storage, no sync, no upload.'}</li>
        </ul>

        <div className="gb-row">
          {!isNative ? (
            <Focusable
              autoFocus
              onSelect={() => void navigate({ to: '/download' })}
              selectLabel="Get the app"
              className="gb-focusable--flat"
            >
              <Button size="3">
                <Icon name="download" size={16} />
                {'Get the app'}
              </Button>
            </Focusable>
          ) : (
            <Text color="gray">
              {
                'This build is for a phone. Open GOOD BUILD on a Windows or Linux desktop to make a mod.'
              }
            </Text>
          )}

          <Focusable
            onSelect={onReference}
            selectLabel="Read the reference"
            className="gb-focusable--flat"
          >
            <Button size="3" variant="ghost">
              <Icon name="book" size={16} />
              {'Read the reference'}
            </Button>
          </Focusable>
        </div>
      </Card>
    </FocusZone>
  );
}
